import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  FormControlLabel,
  Grid,
  Alert,
  Divider,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CategoryIcon from "@mui/icons-material/Category";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  CategoryItem,
  DEFAULT_CATEGORIES,
  AVAILABLE_ICONS,
  renderCategoryIcon,
} from "../../utils/categoryHelper";

interface CategoryManagerProps {
  categories: CategoryItem[];
  onChange: (updatedCategories: CategoryItem[]) => void;
  showSectionSwitch?: boolean;
  showCategorySection?: boolean;
  onShowSectionChange?: (show: boolean) => void;
  onSave?: () => Promise<void> | void;
  isSaving?: boolean;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({
  categories,
  onChange,
  showSectionSwitch = true,
  showCategorySection = true,
  onShowSectionChange,
  onSave,
  isSaving = false,
}) => {
  // Modal state for Create / Edit
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryItem | null>(null);

  // Form fields
  const [formName, setFormName] = useState("");
  const [formSubtitle, setFormSubtitle] = useState("");
  const [formTag, setFormTag] = useState("");
  const [formIcon, setFormIcon] = useState("general");
  const [formIsOffer, setFormIsOffer] = useState(false);
  const [formEnabled, setFormEnabled] = useState(true);
  const [formError, setFormError] = useState("");

  // Confirmation dialog for delete
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<CategoryItem | null>(null);

  // Reset confirmation
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  // Open modal for Create
  const handleOpenCreate = () => {
    setEditingCategory(null);
    setFormName("");
    setFormSubtitle("");
    setFormTag("");
    setFormIcon("general");
    setFormIsOffer(false);
    setFormEnabled(true);
    setFormError("");
    setModalOpen(true);
  };

  // Open modal for Edit
  const handleOpenEdit = (category: CategoryItem) => {
    setEditingCategory(category);
    setFormName(category.name);
    setFormSubtitle(category.subtitle || "");
    setFormTag(category.tag || "");
    setFormIcon(category.icon || "general");
    setFormIsOffer(Boolean(category.isOffer));
    setFormEnabled(category.enabled !== false);
    setFormError("");
    setModalOpen(true);
  };

  // Save Modal
  const handleSaveModal = () => {
    const trimmedName = formName.trim();
    if (!trimmedName) {
      setFormError("Category name is required.");
      return;
    }

    // Check duplicate name
    const isDuplicate = categories.some(
      (c) =>
        c.name.toLowerCase() === trimmedName.toLowerCase() &&
        (!editingCategory || c.id !== editingCategory.id)
    );
    if (isDuplicate) {
      setFormError("A category with this name already exists.");
      return;
    }

    if (editingCategory) {
      // Update
      const updated = categories.map((c) =>
        c.id === editingCategory.id
          ? {
              ...c,
              name: trimmedName,
              subtitle: formSubtitle.trim(),
              tag: formTag.trim(),
              icon: formIcon,
              isOffer: formIsOffer,
              enabled: formEnabled,
            }
          : c
      );
      onChange(updated);
    } else {
      // Create new
      const newCategory: CategoryItem = {
        id: `cat-${Date.now()}-${trimmedName.toLowerCase().replace(/[^a-z0-9]/g, "-")}`,
        name: trimmedName,
        subtitle: formSubtitle.trim(),
        tag: formTag.trim(),
        icon: formIcon,
        isOffer: formIsOffer,
        enabled: formEnabled,
        order: categories.length + 1,
      };
      onChange([...categories, newCategory]);
    }

    setModalOpen(false);
  };

  // Toggle single category enable/disable
  const handleToggleEnable = (id: string) => {
    const updated = categories.map((c) =>
      c.id === id ? { ...c, enabled: !c.enabled } : c
    );
    onChange(updated);
  };

  // Delete category
  const handleConfirmDelete = () => {
    if (categoryToDelete) {
      const updated = categories.filter((c) => c.id !== categoryToDelete.id);
      onChange(updated);
      setDeleteConfirmOpen(false);
      setCategoryToDelete(null);
    }
  };

  // Move up
  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newItems = [...categories];
    const temp = newItems[index - 1];
    newItems[index - 1] = newItems[index];
    newItems[index] = temp;
    onChange(newItems);
  };

  // Move down
  const handleMoveDown = (index: number) => {
    if (index === categories.length - 1) return;
    const newItems = [...categories];
    const temp = newItems[index + 1];
    newItems[index + 1] = newItems[index];
    newItems[index] = temp;
    onChange(newItems);
  };

  // Reset to defaults
  const handleConfirmReset = () => {
    onChange([...DEFAULT_CATEGORIES]);
    setResetConfirmOpen(false);
  };

  const enabledCount = categories.filter((c) => c.enabled).length;

  return (
    <Card
      id="categories"
      sx={{
        borderRadius: 3,
        border: "1px solid #e0e0e0",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                bgcolor: "#f0f4ff",
                color: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CategoryIcon />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Store Categories
                </Typography>
                <Chip
                  label={`${enabledCount} of ${categories.length} Enabled`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: enabledCount > 0 ? "#e0f2fe" : "#f1f5f9",
                    color: enabledCount > 0 ? "#0284c7" : "#64748b",
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Enable, disable, and create categories for your storefront navigation and shop grid
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, alignSelf: { xs: "stretch", sm: "auto" } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={() => setResetConfirmOpen(true)}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Reset Defaults
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                boxShadow: "0 4px 10px rgba(37,99,235,0.25)",
                fontWeight: 600,
              }}
            >
              Create Category
            </Button>
          </Box>
        </Box>

        {/* Storefront Section Visibility Switch */}
        {showSectionSwitch && onShowSectionChange && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2.5,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                Show "Shop by Category" Grid on Storefront
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Display the category cards section between the hero and product catalog on the homepage
              </Typography>
            </Box>
            <Switch
              checked={showCategorySection}
              onChange={(e) => onShowSectionChange(e.target.checked)}
              color="primary"
            />
          </Paper>
        )}

        <Divider sx={{ mb: 2.5 }} />

        {/* Category Items List */}
        {categories.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <CategoryIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#475569" }}>
              No categories configured
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2 }}>
              Add a category or reset to defaults to display categories on your store.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{ textTransform: "none" }}
            >
              Create First Category
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {categories.map((cat, index) => {
              const isSale = cat.isOffer || cat.tag?.toUpperCase() === "SALE";
              return (
                <Paper
                  key={cat.id || index}
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 2.5,
                    border: `1px solid ${cat.enabled ? "#e2e8f0" : "#f1f5f9"}`,
                    bgcolor: cat.enabled ? "#ffffff" : "#f8fafc",
                    opacity: cat.enabled ? 1 : 0.65,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: cat.enabled ? "#cbd5e1" : "#e2e8f0",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    },
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  {/* Left Info */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, width: { xs: "100%", sm: "auto" } }}>
                    {/* Position & Icon */}
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: isSale ? "#fff1f2" : "#f1f5f9",
                        color: isSale ? "#e11d48" : "#334155",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {renderCategoryIcon(cat.icon, { fontSize: 24 })}
                    </Box>

                    {/* Name & Details */}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isSale ? "#be123c" : "#0f172a",
                            fontSize: "0.95rem",
                          }}
                        >
                          {cat.name}
                        </Typography>

                        {/* Tag/Badge */}
                        {cat.tag && (
                          <Chip
                            label={cat.tag}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              bgcolor: isSale ? "#ffe4e6" : "#f1f5f9",
                              color: isSale ? "#e11d48" : "#475569",
                              textTransform: "uppercase",
                            }}
                          />
                        )}

                        {isSale && !cat.tag && (
                          <Chip
                            label="SALE"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              bgcolor: "#ffe4e6",
                              color: "#e11d48",
                            }}
                          />
                        )}
                      </Box>

                      {/* Subtitle */}
                      {cat.subtitle && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748b",
                            display: "block",
                            fontSize: "0.78rem",
                            mt: 0.25,
                          }}
                        >
                          {cat.subtitle}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Right Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "space-between", sm: "flex-end" },
                      width: { xs: "100%", sm: "auto" },
                      gap: { xs: 1, sm: 1.5 },
                      mt: { xs: 1, sm: 0 },
                      pt: { xs: 1, sm: 0 },
                      borderTop: { xs: "1px dashed #e2e8f0", sm: "none" },
                    }}
                  >
                    {/* Status Chip */}
                    <Chip
                      icon={
                        cat.enabled ? (
                          <VisibilityIcon sx={{ "&&": { fontSize: 14 } }} />
                        ) : (
                          <VisibilityOffIcon sx={{ "&&": { fontSize: 14 } }} />
                        )
                      }
                      label={cat.enabled ? "Active" : "Disabled"}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        bgcolor: cat.enabled ? "#dcfce7" : "#f1f5f9",
                        color: cat.enabled ? "#15803d" : "#94a3b8",
                      }}
                    />

                    {/* Enable / Disable Switch */}
                    <Tooltip title={cat.enabled ? "Disable Category" : "Enable Category"}>
                      <Switch
                        size="small"
                        checked={cat.enabled}
                        onChange={() => handleToggleEnable(cat.id)}
                        color="success"
                      />
                    </Tooltip>

                    {/* Reorder Buttons */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Tooltip title="Move Up">
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === 0}
                            onClick={() => handleMoveUp(index)}
                            sx={{ p: 0.5 }}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move Down">
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === categories.length - 1}
                            onClick={() => handleMoveDown(index)}
                            sx={{ p: 0.5 }}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    {/* Edit Button */}
                    <Tooltip title="Edit Category">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(cat)}
                        sx={{
                          p: 0.75,
                          color: "#3b82f6",
                          bgcolor: "#eff6ff",
                          "&:hover": { bgcolor: "#dbeafe" },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {/* Delete Button */}
                    <Tooltip title="Delete Category">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setCategoryToDelete(cat);
                          setDeleteConfirmOpen(true);
                        }}
                        sx={{
                          p: 0.75,
                          color: "#ef4444",
                          bgcolor: "#fef2f2",
                          "&:hover": { bgcolor: "#fee2e2" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Save button if separate onSave is provided */}
        {onSave && (
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={isSaving}
              sx={{
                borderRadius: 2,
                px: 3,
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(135deg, #10b981, #059669)",
                boxShadow: "0 4px 10px rgba(16,185,129,0.25)",
              }}
            >
              {isSaving ? "Saving Categories..." : "Save Categories"}
            </Button>
          </Box>
        )}
      </CardContent>

      {/* CREATE / EDIT DIALOG */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {editingCategory ? "Edit Category" : "Create New Category"}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {formError}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Category Name */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155", mb: 0.5, display: "block" }}>
                Category Name *
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., Hair Care, Fragrances, Skin Care"
                value={formName}
                onChange={(e) => {
                  setFormName(e.target.value);
                  setFormError("");
                }}
              />
            </Box>

            {/* Subtitle / Description */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155", mb: 0.5, display: "block" }}>
                Subtitle / Description
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., Stylers & Dryers, Tested & Certified"
                value={formSubtitle}
                onChange={(e) => setFormSubtitle(e.target.value)}
              />
            </Box>

            {/* Badge / Tag */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155", mb: 0.5, display: "block" }}>
                Badge / Tag Text
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., Salon Grade, SALE, Certified, New"
                value={formTag}
                onChange={(e) => setFormTag(e.target.value)}
              />
            </Box>

            {/* Icon Picker */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155", mb: 1, display: "block" }}>
                Select Category Icon
              </Typography>
              <Grid container spacing={1}>
                {AVAILABLE_ICONS.map((iconOpt) => {
                  const isSelected = formIcon === iconOpt.key;
                  return (
                    <Grid item xs={6} sm={4} key={iconOpt.key}>
                      <Paper
                        elevation={0}
                        onClick={() => setFormIcon(iconOpt.key)}
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          border: `1.5px solid ${isSelected ? "#2563eb" : "#e2e8f0"}`,
                          bgcolor: isSelected ? "#eff6ff" : "#f8fafc",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            borderColor: "#3b82f6",
                            bgcolor: "#f0f7ff",
                          },
                        }}
                      >
                        <Box sx={{ color: isSelected ? "#2563eb" : "#64748b", display: "flex" }}>
                          <iconOpt.Icon fontSize="small" />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: isSelected ? 700 : 500,
                            color: isSelected ? "#1e40af" : "#334155",
                            fontSize: "0.75rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {iconOpt.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* Toggles */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formIsOffer}
                    onChange={(e) => setFormIsOffer(e.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Highlight as Special Offer / SALE
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Applies high-visibility red badge styling to draw customer attention
                    </Typography>
                  </Box>
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={formEnabled}
                    onChange={(e) => setFormEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Enable Category on Storefront
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      When enabled, this category will appear in header navigation and the category grid
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveModal}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
            }}
          >
            {editingCategory ? "Update Category" : "Add Category"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Category?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete the category{" "}
            <strong>"{categoryToDelete?.name}"</strong>? It will no longer appear on your storefront.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* RESET CONFIRMATION DIALOG */}
      <Dialog
        open={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Reset to Rudrix Defaults?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will restore the 5 standard categories:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1, color: "text.secondary", fontSize: "0.85rem" }}>
            <li>Hair Care</li>
            <li>Personal Care</li>
            <li>Wellness &amp; Health</li>
            <li>Refurbished Tech</li>
            <li>Special Offers (SALE)</li>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setResetConfirmOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmReset}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CategoryManager;
